import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, AlertTriangle, FolderTree, PauseCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 4000;

export function CosReceiptRescuePanel() {
  const [previewPolling, setPreviewPolling] = useState(false);
  const [applyPolling, setApplyPolling] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [target, setTarget] = useState('missing');
  const [driveSettings, setDriveSettings] = useState(null);
  const [pauseStatus, setPauseStatus] = useState(null);
  const [pauseToggling, setPauseToggling] = useState(false);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.driveSettingsGet();
        setDriveSettings(data);
      } catch {
        // Non-fatal: the destination banner just won't render; Apply will
        // still surface the real error if the root turns out to be unset.
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.cosReceiptRescuePauseStatus();
        setPauseStatus(data);
      } catch {
        // Non-fatal: the toggle just won't render.
      }
    })();
  }, []);

  const togglePause = async () => {
    setPauseToggling(true);
    try {
      const { data } = await diagnosticsAPI.cosReceiptRescueSetPause(!pauseStatus?.paused);
      setPauseStatus(data);
      toast.success(data.paused ? 'Scheduled rescue paused' : 'Scheduled rescue resumed');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPauseToggling(false);
    }
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
    setPreviewPolling(false);
    setApplyPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // On mount: recover a previous apply job so a page refresh doesn't lose
  // the result of a long-running rescue.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.cosReceiptRescueJobLatest('apply');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setApplyResult(job.result);
        } else if (job.status === 'running') {
          startTimeRef.current = Date.now();
          startPolling(job._id, 'apply');
        } else if (job.status === 'failed') {
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback((id, operation) => {
    stopPolling();
    if (operation === 'preview') setPreviewPolling(true);
    else setApplyPolling(true);

    startTimeRef.current = startTimeRef.current ?? Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.cosReceiptRescueJobStatus(id);
        if (data.status === 'completed') {
          stopPolling();
          startTimeRef.current = null;
          if (operation === 'preview') {
            setPreview(data.result);
            toast.success('Preview complete');
          } else {
            setApplyResult(data.result);
            setApplyFailed(null);
            toast.success(
              `Rescue complete: ${data.result?.stored ?? 0} stored, ` +
              `${data.result?.failed ?? 0} failed`
            );
            setPreview(null);
          }
        } else if (data.status === 'failed') {
          stopPolling();
          startTimeRef.current = null;
          if (operation === 'apply') setApplyFailed(data.error);
          toast.error(data.error ?? `${operation} failed`);
        }
      } catch (err) {
        stopPolling();
        startTimeRef.current = null;
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const runPreview = async () => {
    setDispatching(true);
    setPreview(null);
    setApplyResult(null);
    setApplyFailed(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.cosReceiptRescuePreviewStart(target);
      startPolling(data.job_id, 'preview');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runApply = async () => {
    const count = preview ? preview.rescuable_count : null;
    const pauseWarning = pauseStatus?.paused
      ? '\n\nThe scheduled rescue is currently PAUSED. Running Apply now overrides that pause for this one run only.'
      : '';
    if (!window.confirm(
      (count != null
        ? `This will fetch ${count} receipt(s) from Twilio and file them into Drive. Continue?`
        : `This will attempt to rescue CoS receipts with an expiring Twilio media URL (target: ${target}). Continue?`
      ) + pauseWarning
    )) return;

    setDispatching(true);
    setApplyResult(null);
    setApplyFailed(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.cosReceiptRescueApplyStart(target);
      startPolling(data.job_id, 'apply');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const busy = dispatching || previewPolling || applyPolling;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">CoS Receipt Rescue (Twilio Media)</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          Chief-of-Staff receipts whose only copy is an expiring Twilio media URL: fetches
          each one, validates it's a real image/PDF, and files it into the same Drive tree
          the Pennylane pull uses, then stamps file_url. Preview live-checks Twilio without
          writing; Apply re-checks fresh and pulls. Also runs automatically every 6 hours
          for newly-eligible receipts, but only once Drive is configured -- it runs after
          Drive is configured, not before.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ── Pause control: scheduled 6-hourly rescue only, manual Preview/Apply unaffected ── */}
        {pauseStatus && (
          <div className={`border rounded-lg p-2 flex items-center gap-3 ${pauseStatus.paused ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20' : 'bg-muted/30'}`}>
            <Switch
              checked={pauseStatus.paused}
              onCheckedChange={togglePause}
              disabled={pauseToggling}
              aria-label="Pause scheduled CoS receipt rescue"
            />
            <div className="min-w-0">
              <p className="text-xs font-medium flex items-center gap-1">
                {pauseStatus.paused && <PauseCircle className="h-3.5 w-3.5 text-amber-600" />}
                {pauseStatus.paused ? 'Scheduled rescue paused' : 'Scheduled rescue active (every 6 hours)'}
              </p>
              {pauseStatus.paused && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                  Paused by {pauseStatus.paused_by} at {pauseStatus.paused_at}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── Destination path: where these files are about to land ── */}
        {driveSettings && (
          driveSettings.configured ? (
            <div className="border rounded-lg p-2 flex items-start gap-2 bg-muted/30">
              <FolderTree className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs font-medium">Destination</p>
                <p className="text-xs text-muted-foreground font-mono break-all">
                  {driveSettings.drive_id ? (driveSettings.drive_name || driveSettings.drive_id) : 'My Drive'}
                  {' › '}{driveSettings.root_folder_name || driveSettings.root_folder_id}
                  {' › '}Jurisdiction {' › '}Entity{' › '}Year{' › '}Month{' › '}Receipts
                </p>
              </div>
            </div>
          ) : (
            <div className="border-2 rounded-lg p-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Drive destination root is not configured. This runs after Drive is configured
                under Integrations &gt; Email &amp; Drive -- Apply and the scheduled rescue
                both no-op until then.
              </p>
            </div>
          )
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={runPreview} disabled={busy} size="sm">
            {previewPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Preview
          </Button>

          <select
            value={target}
            onChange={(e) => { setTarget(e.target.value); setPreview(null); }}
            disabled={busy}
            className="h-9 rounded-md border border-input bg-background px-2 text-xs"
          >
            <option value="missing">All missing</option>
            <option value="retryable">Retry pending/failed only</option>
          </select>

          <Button
            onClick={runApply}
            disabled={busy || (driveSettings && !driveSettings.configured)}
            size="sm"
            variant={preview && preview.rescuable_count > 0 ? 'default' : 'outline'}
          >
            {applyPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {preview ? `Apply (${fmtInt(preview.rescuable_count)} rescuable)` : 'Apply'}
          </Button>

          {(previewPolling || applyPolling) && (
            <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              {elapsed}s
            </span>
          )}
        </div>

        {/* ── Preview result ── */}
        {preview && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              PREVIEW (read-only, target: {preview.target})
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Pending rescue</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(preview.total_pending)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Rescuable from Twilio</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(preview.rescuable_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Entity unresolved</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(preview.entity_unresolved_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Fetch failed</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(preview.fetch_failed_count)}</p>
              </div>
            </div>

            {preview.rescuable_samples && preview.rescuable_samples.length > 0 && (
              <details open>
                <summary className="text-sm font-medium cursor-pointer">
                  Rescuable receipts ({preview.rescuable_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.rescuable_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{s.source_id}</td>
                          <td className="px-2 py-1">{s.supplier_client_name}</td>
                          <td className="px-2 py-1 text-muted-foreground">{s.destination_path}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {preview.entity_unresolved_samples && preview.entity_unresolved_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Entity-unresolved samples ({preview.entity_unresolved_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.entity_unresolved_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{s.source_id}</td>
                          <td className="px-2 py-1">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {preview.fetch_failed_samples && preview.fetch_failed_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Fetch-failed samples ({preview.fetch_failed_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.fetch_failed_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{s.source_id}</td>
                          <td className="px-2 py-1">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">Generated: {preview.generated_at}</p>
          </div>
        )}

        {/* ── Apply failed (systemic, e.g. Drive not connected) ── */}
        {applyFailed && (
          <div className="space-y-2">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              APPLY FAILED
            </Badge>
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-600">{applyFailed}</p>
            </div>
          </div>
        )}

        {/* ── Apply result ── */}
        {applyResult && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              APPLIED ({applyResult.target})
            </Badge>

            <div className="grid grid-cols-3 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Attempted</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyResult.total_attempted)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Stored</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(applyResult.stored)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Failed</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(applyResult.failed)}</p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              {applyResult.elapsed_seconds}s elapsed &middot; {applyResult.throughput_per_minute ?? '-'} receipts/min
            </p>

            {applyResult.failed_samples && applyResult.failed_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Failed samples ({applyResult.failed_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {applyResult.failed_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{s.source_id}</td>
                          <td className="px-2 py-1">{s.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Started: {applyResult.started_at} | Finished: {applyResult.finished_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
